import * as React from "react";
import { Upload, X, AlertCircle, Loader2, CheckCircle2, Image as ImageIcon } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useDirectUpload } from "@/lib/branding/useDirectUpload";
import { useDeleteBrandingAsset } from "@/lib/branding/api";
import type { AssetKind } from "@/lib/branding/types";

interface AssetUploaderProps {
  kind: AssetKind;
  title: string;
  description: string;
  acceptMimeTypes: readonly string[];
  acceptHelp: string;
  maxBytes: number;
  /** Currently committed URL — null when no upload yet. */
  currentUrl: string | null;
  /** Called with the new canonical URL after a successful direct upload.
   * Parent decides when to call commit (usually batched with colour saves). */
  onUploaded: (publicUrl: string) => void;
  /** Called after the IA confirms "remove" — parent re-fetches branding. */
  onDeleted: () => void;
  /** Optional preview frame (e.g., favicon SVG fallback). */
  fallbackPreview?: React.ReactNode;
}

function bytesToHuman(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Asset uploader (Phase F2b — IA self-serve twin of the SA component).
 *
 * Identical UX to the super-admin uploader, but the mint + delete calls
 * are IA-scoped (`/me/branding/*`), so the tenant is resolved from the
 * JWT rather than a slug prop.
 */
export function AssetUploader({
  kind,
  title,
  description,
  acceptMimeTypes,
  acceptHelp,
  maxBytes,
  currentUrl,
  onUploaded,
  onDeleted,
  fallbackPreview,
}: AssetUploaderProps) {
  const upload = useDirectUpload(kind);
  const del = useDeleteBrandingAsset();
  const [dragOver, setDragOver] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const validate = (file: File): string | null => {
    if (!acceptMimeTypes.includes(file.type)) {
      return `Unsupported file type: ${file.type || "unknown"}. Allowed: ${acceptHelp}.`;
    }
    if (file.size > maxBytes) {
      return `File is ${bytesToHuman(file.size)}; max allowed is ${bytesToHuman(maxBytes)}.`;
    }
    if (file.size === 0) {
      return "File is empty.";
    }
    return null;
  };

  const onFile = async (file: File) => {
    setLocalError(null);
    const err = validate(file);
    if (err) {
      setLocalError(err);
      return;
    }
    try {
      const url = await upload.upload(file);
      onUploaded(url);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Upload failed.");
    }
  };

  const onDelete = async () => {
    try {
      await del.mutateAsync(kind);
      onDeleted();
      upload.reset();
    } catch (e) {
      setLocalError(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const showProgress = upload.state === "uploading" || upload.state === "minting";
  const errorText = localError ?? upload.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {currentUrl ? (
          <div className="flex items-start gap-4">
            <div className="bg-muted/30 grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-md border">
              <img
                src={currentUrl}
                alt={`Current ${kind}`}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="grid gap-2 text-sm">
              <p className="font-medium">Currently uploaded</p>
              <p className="text-muted-foreground break-all text-xs">{currentUrl}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => inputRef.current?.click()}
                  disabled={showProgress}
                >
                  <Upload className="size-3.5" aria-hidden="true" />
                  Replace
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  disabled={del.isPending || showProgress}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="size-3.5" aria-hidden="true" />
                  {del.isPending ? "Removing…" : "Remove"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-4">
            <div className="bg-muted/30 grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-md border">
              {fallbackPreview ?? (
                <ImageIcon className="text-muted-foreground size-8" aria-hidden="true" />
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              No {kind} uploaded yet. Drop or click to upload.
            </p>
          </div>
        )}

        <label
          className={cn(
            "grid cursor-pointer place-items-center gap-2 rounded-md border-2 border-dashed px-4 py-6 transition",
            dragOver
              ? "border-primary bg-primary/5 scale-[1.01]"
              : "border-border hover:border-primary/40 hover:bg-muted/30",
            showProgress && "pointer-events-none opacity-70",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files[0];
            if (f) void onFile(f);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={acceptMimeTypes.join(",")}
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              e.target.value = ""; // allow re-upload of same filename
            }}
          />
          {showProgress ? (
            <>
              <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden="true" />
              <p className="text-muted-foreground text-xs">
                {upload.state === "minting" ? "Preparing upload…" : `Uploading ${upload.progress}%`}
              </p>
              {upload.state === "uploading" && (
                <div className="bg-muted h-1 w-full overflow-hidden rounded">
                  <div
                    className="bg-primary h-full transition-[width] duration-150"
                    style={{ width: `${upload.progress}%` }}
                    aria-hidden="true"
                  />
                </div>
              )}
            </>
          ) : upload.state === "done" ? (
            <>
              <CheckCircle2 className="size-5 text-emerald-600" aria-hidden="true" />
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                Uploaded — click Save to publish.
              </p>
            </>
          ) : (
            <>
              <Upload className="text-muted-foreground size-5" aria-hidden="true" />
              <p className="text-sm font-medium">Drop a file here or click to browse</p>
              <p className="text-muted-foreground text-xs">
                {acceptHelp} · max {bytesToHuman(maxBytes)}
              </p>
            </>
          )}
        </label>

        {errorText && (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          >
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{errorText}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
